<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Document Preview
          </h1>
          <p class="text-gray-600 mt-1">Generate previews of closing documents before finalizing.</p>
        </div>
        <router-link to="/dashboard" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Back
        </router-link>
      </div>

      <!-- Tolerance Check -->
      <ToleranceBanner :tolerance-status="toleranceStatus" />

      <!-- Required Preview -->
      <div class="card border-2 border-primary-200">
        <div class="card-header flex items-center justify-between bg-primary-50">
          <div class="flex items-center">
            <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
              <font-awesome-icon icon="file-invoice-dollar" class="text-white" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">HUD-1 Settlement Statement</h3>
              <p class="text-sm text-primary-600 font-medium">Required before continuing</p>
            </div>
          </div>
          <span v-if="hudPreviewGenerated" class="status-badge status-completed">
            <font-awesome-icon icon="check" class="mr-1" />
            Generated
          </span>
        </div>
        <div class="card-body">
          <p class="text-gray-600 text-sm mb-4">
            Generate a preview of the HUD-1 Settlement Statement to review all closing costs and fees before finalizing.
          </p>

          <!-- HUD Preview -->
          <div v-if="hudPreviewUrl" class="bg-gray-50 rounded-lg p-4 mb-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <font-awesome-icon icon="file-pdf" class="text-primary-600 text-xl mr-3" />
                <div>
                  <p class="font-medium text-gray-900">HUD-1 Preview</p>
                  <p class="text-sm text-gray-500">Generated {{ formatDate(hudPreviewDate) }}</p>
                </div>
              </div>
              <a 
                :href="hudPreviewUrl" 
                target="_blank" 
                class="btn btn-secondary btn-sm"
              >
                <font-awesome-icon icon="eye" class="mr-2" />
                View
              </a>
            </div>
          </div>

          <button
            @click="generateHudPreview"
            class="btn btn-primary w-full"
            :disabled="isGeneratingHud"
          >
            <font-awesome-icon v-if="isGeneratingHud" icon="spinner" class="mr-2 animate-spin" />
            <font-awesome-icon v-else icon="file-alt" class="mr-2" />
            {{ hudPreviewUrl ? 'Regenerate HUD-1 Preview' : 'Generate HUD-1 Preview' }}
          </button>

          <div v-if="hudError" class="mt-3 tolerance-exceeded rounded-lg p-3">
            <span class="text-sm text-danger-800">{{ hudError }}</span>
          </div>
        </div>
      </div>

      <!-- Optional Previews -->
      <h2 class="text-lg font-semibold text-gray-700">Optional Previews</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Security Instruments Preview -->
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <font-awesome-icon icon="file-contract" class="text-gray-600" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Security Instruments</h3>
                <p class="text-sm text-gray-500">Mortgage/Deed of Trust</p>
              </div>
            </div>
            <span v-if="securityPreviewGenerated" class="status-badge status-completed">
              <font-awesome-icon icon="check" class="mr-1" />
              Generated
            </span>
          </div>
          <div class="card-body">
            <p class="text-gray-600 text-sm mb-4">
              Preview the security instruments including the Mortgage or Deed of Trust.
            </p>

            <!-- Security Preview -->
            <div v-if="securityPreviewUrl" class="bg-gray-50 rounded-lg p-4 mb-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <font-awesome-icon icon="file-pdf" class="text-primary-600 text-xl mr-3" />
                  <div>
                    <p class="font-medium text-gray-900">Security Instruments</p>
                    <p class="text-sm text-gray-500">Generated {{ formatDate(securityPreviewDate) }}</p>
                  </div>
                </div>
                <a 
                  :href="securityPreviewUrl" 
                  target="_blank" 
                  class="btn btn-secondary btn-sm"
                >
                  <font-awesome-icon icon="eye" class="mr-2" />
                  View
                </a>
              </div>
            </div>

            <button
              @click="generateSecurityPreview"
              class="btn btn-secondary w-full"
              :disabled="isGeneratingSecurity"
            >
              <font-awesome-icon v-if="isGeneratingSecurity" icon="spinner" class="mr-2 animate-spin" />
              <font-awesome-icon v-else icon="file-signature" class="mr-2" />
              {{ securityPreviewUrl ? 'Regenerate Preview' : 'Generate Preview' }}
            </button>

            <div v-if="securityError" class="mt-3 tolerance-exceeded rounded-lg p-3">
              <span class="text-sm text-danger-800">{{ securityError }}</span>
            </div>
          </div>
        </div>

        <!-- Closing Instructions Preview -->
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <font-awesome-icon icon="clipboard-list" class="text-gray-600" />
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Closing Instructions</h3>
                <p class="text-sm text-gray-500">Settlement instructions</p>
              </div>
            </div>
            <span v-if="instructionsPreviewGenerated" class="status-badge status-completed">
              <font-awesome-icon icon="check" class="mr-1" />
              Generated
            </span>
          </div>
          <div class="card-body">
            <p class="text-gray-600 text-sm mb-4">
              Preview the closing instructions document with settlement details and requirements.
            </p>

            <!-- Instructions Preview -->
            <div v-if="instructionsPreviewUrl" class="bg-gray-50 rounded-lg p-4 mb-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <font-awesome-icon icon="file-pdf" class="text-primary-600 text-xl mr-3" />
                  <div>
                    <p class="font-medium text-gray-900">Closing Instructions</p>
                    <p class="text-sm text-gray-500">Generated {{ formatDate(instructionsPreviewDate) }}</p>
                  </div>
                </div>
                <a 
                  :href="instructionsPreviewUrl" 
                  target="_blank" 
                  class="btn btn-secondary btn-sm"
                >
                  <font-awesome-icon icon="eye" class="mr-2" />
                  View
                </a>
              </div>
            </div>

            <button
              @click="generateInstructionsPreview"
              class="btn btn-secondary w-full"
              :disabled="isGeneratingInstructions"
            >
              <font-awesome-icon v-if="isGeneratingInstructions" icon="spinner" class="mr-2 animate-spin" />
              <font-awesome-icon v-else icon="clipboard-list" class="mr-2" />
              {{ instructionsPreviewUrl ? 'Regenerate Preview' : 'Generate Preview' }}
            </button>

            <div v-if="instructionsError" class="mt-3 tolerance-exceeded rounded-lg p-3">
              <span class="text-sm text-danger-800">{{ instructionsError }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Summary -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold text-gray-900">Preview Status</h3>
        </div>
        <div class="card-body">
          <div class="space-y-3">
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="hudPreviewGenerated ? 'bg-success-100' : 'bg-danger-100'"
              >
                <font-awesome-icon
                  :icon="hudPreviewGenerated ? 'check' : 'times'"
                  :class="hudPreviewGenerated ? 'text-success-600' : 'text-danger-600'"
                  class="text-xs"
                />
              </div>
              <span :class="hudPreviewGenerated ? 'text-gray-900' : 'text-danger-600 font-medium'">
                HUD-1 Settlement Statement previewed
                <span v-if="!hudPreviewGenerated" class="text-xs">(required)</span>
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="securityPreviewGenerated ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="securityPreviewGenerated ? 'check' : 'minus'"
                  :class="securityPreviewGenerated ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="securityPreviewGenerated ? 'text-gray-900' : 'text-gray-500'">
                Security instruments previewed
                <span class="text-xs text-gray-400">(optional)</span>
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="instructionsPreviewGenerated ? 'bg-success-100' : 'bg-gray-100'"
              >
                <font-awesome-icon
                  :icon="instructionsPreviewGenerated ? 'check' : 'minus'"
                  :class="instructionsPreviewGenerated ? 'text-success-600' : 'text-gray-400'"
                  class="text-xs"
                />
              </div>
              <span :class="instructionsPreviewGenerated ? 'text-gray-900' : 'text-gray-500'">
                Closing instructions previewed
                <span class="text-xs text-gray-400">(optional)</span>
              </span>
            </div>
            <div class="flex items-center">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                :class="!hasToleranceViolation ? 'bg-success-100' : 'bg-danger-100'"
              >
                <font-awesome-icon
                  :icon="!hasToleranceViolation ? 'check' : 'times'"
                  :class="!hasToleranceViolation ? 'text-success-600' : 'text-danger-600'"
                  class="text-xs"
                />
              </div>
              <span :class="!hasToleranceViolation ? 'text-gray-900' : 'text-danger-600'">
                Fees within tolerance
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <router-link to="/payoffs" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Payoffs
        </router-link>
        <button 
          @click="continueToNext" 
          class="btn btn-primary"
          :disabled="!canContinue"
        >
          Continue to Generate Documents
          <font-awesome-icon icon="arrow-right" class="ml-2" />
        </button>
      </div>

      <p v-if="!canContinue" class="text-sm text-danger-600 text-center font-medium">
        <font-awesome-icon icon="exclamation-circle" class="mr-1" />
        Generate the HUD-1 preview before continuing.
      </p>
    </div>
  </TitleLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import ToleranceBanner from '@/components/ToleranceBanner/index.vue'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Preview',
  components: {
    TitleLayout,
    ToleranceBanner,
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    // HUD-1 Preview state (required)
    const hudPreviewUrl = ref(null)
    const hudPreviewDate = ref(null)
    const hudPreviewGenerated = ref(false)
    const isGeneratingHud = ref(false)
    const hudError = ref(null)

    // Security Instruments Preview state (optional)
    const securityPreviewUrl = ref(null)
    const securityPreviewDate = ref(null)
    const securityPreviewGenerated = ref(false)
    const isGeneratingSecurity = ref(false)
    const securityError = ref(null)

    // Closing Instructions Preview state (optional)
    const instructionsPreviewUrl = ref(null)
    const instructionsPreviewDate = ref(null)
    const instructionsPreviewGenerated = ref(false)
    const isGeneratingInstructions = ref(false)
    const instructionsError = ref(null)

    const toleranceStatus = computed(() => store.getters.toleranceStatus)
    const hasToleranceViolation = computed(() => store.getters.hasToleranceViolation)

    // Only HUD-1 is required
    const canContinue = computed(() => {
      return hudPreviewGenerated.value
    })

    const generateHudPreview = async () => {
      isGeneratingHud.value = true
      hudError.value = null

      try {
        const data = await apiService.generateHudPreview()
        console.log('[Preview] HUD preview generated:', data)

        // Backend returns preview_url (S3 presigned URL) and generated_at
        hudPreviewUrl.value = data.preview_url || data.file_url || '#'
        hudPreviewDate.value = data.generated_at ? new Date(data.generated_at) : new Date()
        hudPreviewGenerated.value = true

        // Mark preview as complete since HUD is required
        store.dispatch('setClosingProgress', { previewGenerated: true })

      } catch (err) {
        console.error('[Preview] HUD generation failed:', err)
        hudError.value = err.response?.data?.detail || 'Failed to generate HUD-1 preview. Please try again.'
      } finally {
        isGeneratingHud.value = false
      }
    }

    const generateSecurityPreview = async () => {
      isGeneratingSecurity.value = true
      securityError.value = null

      try {
        const data = await apiService.generateSecurityPreview()
        console.log('[Preview] Security preview generated:', data)

        // Backend returns preview_url (S3 presigned URL) and generated_at
        securityPreviewUrl.value = data.preview_url || data.file_url || '#'
        securityPreviewDate.value = data.generated_at ? new Date(data.generated_at) : new Date()
        securityPreviewGenerated.value = true

      } catch (err) {
        console.error('[Preview] Security generation failed:', err)
        // Handle 404 gracefully (endpoint may not be implemented)
        if (err.response?.status === 404) {
          securityError.value = 'Security instruments preview is not yet available.'
        } else {
          securityError.value = err.response?.data?.detail || 'Failed to generate security instruments preview. Please try again.'
        }
      } finally {
        isGeneratingSecurity.value = false
      }
    }

    const generateInstructionsPreview = async () => {
      isGeneratingInstructions.value = true
      instructionsError.value = null

      try {
        const data = await apiService.generateInstructionsPreview()
        console.log('[Preview] Instructions preview generated:', data)

        // Backend returns preview_url (S3 presigned URL) and generated_at
        instructionsPreviewUrl.value = data.preview_url || data.file_url || '#'
        instructionsPreviewDate.value = data.generated_at ? new Date(data.generated_at) : new Date()
        instructionsPreviewGenerated.value = true

      } catch (err) {
        console.error('[Preview] Instructions generation failed:', err)
        // Handle 404 gracefully (endpoint may not be implemented)
        if (err.response?.status === 404) {
          instructionsError.value = 'Closing instructions preview is not yet available.'
        } else {
          instructionsError.value = err.response?.data?.detail || 'Failed to generate closing instructions preview. Please try again.'
        }
      } finally {
        isGeneratingInstructions.value = false
      }
    }

    const continueToNext = () => {
      store.dispatch('setClosingProgress', { previewGenerated: true })
      router.push('/documents')
    }

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    }

    const fetchToleranceStatus = async () => {
      try {
        const data = await apiService.getToleranceStatus()
        store.dispatch('setToleranceStatus', data)
      } catch (err) {
        console.error('[Preview] Failed to fetch tolerance status:', err)
      }
    }

    onMounted(() => {
      fetchToleranceStatus()
    })

    return {
      hudPreviewUrl,
      hudPreviewDate,
      hudPreviewGenerated,
      isGeneratingHud,
      hudError,
      securityPreviewUrl,
      securityPreviewDate,
      securityPreviewGenerated,
      isGeneratingSecurity,
      securityError,
      instructionsPreviewUrl,
      instructionsPreviewDate,
      instructionsPreviewGenerated,
      isGeneratingInstructions,
      instructionsError,
      toleranceStatus,
      hasToleranceViolation,
      canContinue,
      generateHudPreview,
      generateSecurityPreview,
      generateInstructionsPreview,
      continueToNext,
      formatDate,
    }
  },
}
</script>
